import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import {
    DeletePositionCommand,
    EditPositionCommand,
    useDeleteSelfcareCartItemByIdMutation,
    useDeleteSelfcareOrderPositionMutation,
    useEditSelfcareOrderPositionMutation,
    useListSelfcareOrderPositionsQuery,
    useRevertSelfcareOrderPositionMutation,
} from 'store/api-slice/hooks'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { LanguageType } from 'store/steps'
import { ErrorField, handleError } from 'helpers/errors'
import { PromiseResponse } from 'helpers/types'
import {
    setAccomodationInSelfcareCart,
    setPositions,
} from 'store/selfcare-slice'
import { useSelfcareCart } from 'store/selfcare-cart-slice/hooks'
import { getLanguageFromLocale } from 'helpers/i18n'

export const useSelfcare = (): {
    refetchPositions: () => void
    editItem: (
        editPositionCommand: EditPositionCommand
    ) => Promise<PromiseResponse>
    isEditProcessing: boolean
    deleteItem: (
        deletePositionCommand: DeletePositionCommand
    ) => Promise<PromiseResponse>
    revertDeleteItem: (modificationId: string) => Promise<void>
    isSelfcareSummaryFetching: boolean
    removeShipping: () => Promise<void>
} => {
    const { shipping } = useAppSelector((state) => state.selfcareCart.items)
    const { locale } = useIntl()
    const language: LanguageType = getLanguageFromLocale(locale)

    const dispatch = useAppDispatch()
    const { refetchSelfcareCart, isSelfcareCartFetching } = useSelfcareCart()

    const [deletePosition] = useDeleteSelfcareOrderPositionMutation()
    const [revertPosition] = useRevertSelfcareOrderPositionMutation()
    const [editPosition, editStatus] = useEditSelfcareOrderPositionMutation()
    const [deleteCartItem] = useDeleteSelfcareCartItemByIdMutation()

    const isEditProcessing = editStatus.isLoading || isSelfcareCartFetching

    const { data, isFetching, refetch } = useListSelfcareOrderPositionsQuery({
        _locale: language,
    })

    const isSelfcareSummaryFetching = isFetching
    useEffect(() => {
        if (isFetching || !data?.positions) return

        dispatch(setPositions(data.positions))
        dispatch(setAccomodationInSelfcareCart(data.hasAccommodation))
    }, [isFetching])

    const refetchPositions = () => {
        refetch()
        refetchSelfcareCart()
    }

    const deleteItem = async (deletePositionCommand: DeletePositionCommand) => {
        try {
            handleError(
                await deletePosition({
                    _locale: language,
                    deletePositionCommand,
                })
            )
            refetchPositions()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const editItem = async (editPositionCommand: EditPositionCommand) => {
        try {
            handleError(
                await editPosition({
                    _locale: language,
                    editPositionCommand,
                })
            )
            refetchPositions()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const revertDeleteItem = async (modificationId: string) => {
        await revertPosition({
            _locale: language,
            modificationId,
            revertPositionCommand: {
                modificationId,
            },
        })

        refetchPositions()
    }

    const removeShipping = async () => {
        const { cartItemId } = shipping || {}
        if (!cartItemId) return

        await deleteCartItem({
            cartItemId,
            _locale: language,
        })

        refetchSelfcareCart()
    }

    return {
        refetchPositions,
        deleteItem,
        editItem,
        isEditProcessing,
        revertDeleteItem,
        isSelfcareSummaryFetching,
        removeShipping,
    }
}
